import { Helmet } from "react-helmet-async";
import { useContent } from "@/context/ContentContext";

/**
 * Injects LocalBusiness JSON-LD + homepage OG/canonical meta.
 * Read by Google, Bing, Facebook, LinkedIn, iMessage, Slack for rich previews.
 */
export default function HomeSeo() {
  const { content } = useContent();
  if (!content) return null;

  const { hero = {}, about = {}, contact = {}, services = [], testimonials = [], gallery = [] } = content;

  // Prefer configured production site URL so canonical/OG tags always resolve
  // to lcbuildersllctx.com even when the site is being crawled via the preview
  // URL. Falls back to the current origin during local dev.
  const configuredSite = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");
  const origin = configuredSite || (typeof window !== "undefined" ? window.location.origin : "");
  const description = (hero.subtitle || about.intro || "").slice(0, 200) ||
    "Metal buildings, carports, fences, and welding by LC Builders LLC in Central Texas.";
  const featured = gallery.find((g) => g.featured) || gallery[0];
  const heroImg = hero.image_data || hero.image_url;
  const previewImg =
    (heroImg && !heroImg.startsWith("data:") && heroImg) ||
    (featured && (featured.image_url || (featured.image_data?.startsWith("data:") ? null : featured.image_data)));

  const ld = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "LC Builders LLC",
    description,
    url: origin,
    telephone: contact.phone || "(254) 833-7563",
    email: contact.email || "lcbuilders87@yahoo.com",
    priceRange: "$$",
    image: previewImg || undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: contact.address_street || undefined,
      addressLocality: contact.address_city || undefined,
      addressRegion: contact.address_state || "TX",
      postalCode: contact.address_postal || undefined,
      addressCountry: "US",
    },
    // If we have a specific list of cities, expose each as its own Place.
    // Google reads this for local-pack ranking. Falls back to a single
    // Place if no explicit list was configured yet.
    areaServed: (contact.service_areas || []).length
      ? contact.service_areas.map((city) => ({
          "@type": "Place",
          name: `${city}, TX`,
        }))
      : { "@type": "Place", name: contact.area || "Central Texas" },
    // Machine-readable hours (24h). Renders as one entry covering all 7
    // days if the same open/close applies daily — mirrors the GBP config.
    openingHoursSpecification:
      contact.hours_open && contact.hours_close
        ? [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: contact.hours_open,
              closes: contact.hours_close,
            },
          ]
        : undefined,
    // sameAs links other Google-visible profiles (Facebook, GBP) back to
    // this site — helps Google merge the site + GBP + FB into one entity.
    sameAs: [contact.facebook_url, contact.google_profile_url]
      .filter((u) => u && String(u).trim())
      .map((u) => u.trim()),
    founder: about.owner_name
      ? { "@type": "Person", name: about.owner_name, jobTitle: about.owner_title || "Owner" }
      : undefined,
    foundingDate: about.years
      ? String(new Date().getFullYear() - Number(about.years))
      : undefined,
    aggregateRating:
      // Prefer real Google Business Profile numbers (owner-maintained via
      // Admin → Contact Info). Falls back to testimonial count so the site
      // still emits a valid aggregateRating before Hondo enters his GBP data.
      Number(contact.google_review_count) > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: String(Number(contact.google_rating) || 5.0),
            reviewCount: String(Number(contact.google_review_count)),
            bestRating: "5",
            worstRating: "1",
          }
        : testimonials.length
        ? {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: String(testimonials.length),
            bestRating: "5",
            worstRating: "1",
          }
        : undefined,
    review: testimonials.slice(0, 5).map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewBody: t.quote,
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    })),
    hasOfferCatalog: services.length
      ? {
          "@type": "OfferCatalog",
          name: "Services",
          itemListElement: services.map((s) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: s.title, description: s.description },
          })),
        }
      : undefined,
  };
  // Strip undefined + empty arrays
  const cleaned = JSON.parse(
    JSON.stringify(ld, (_k, v) => {
      if (Array.isArray(v) && v.length === 0) return undefined;
      return v;
    })
  );

  const title = "LC Builders LLC — Metal Buildings, Carports, Fences & Welding · Central Texas";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`${origin}/`} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${origin}/`} />
      {previewImg && <meta property="og:image" content={previewImg} />}
      <meta property="og:site_name" content="LC Builders LLC" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {previewImg && <meta name="twitter:image" content={previewImg} />}
      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(cleaned)}</script>
    </Helmet>
  );
}
